import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Icon } from '../components/ui/Icons'
import { useSocioPortal } from '../contexts/SocioPortalContext'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_PATTERN = /^\+?[0-9][0-9 ()-]{6,18}$/
const SAVE_ERROR = 'No fue posible guardar los cambios.'

const inputClass = 'mt-2 block min-h-12 w-full rounded-xl border border-border bg-white px-4 py-3 text-base outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15'

function SectionTitle({ children }) {
  return <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{children}</h2>
}

function ReadOnly({ label, value }) {
  return <div><dt className="text-xs font-bold uppercase tracking-wide text-text-secondary">{label}</dt><dd className="mt-1 break-words font-medium">{value || '—'}</dd></div>
}

function CheckRow({ checked, onChange, label, detail }) {
  return (
    <label className={'flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition ' + (checked ? 'border-secondary bg-secondary/10' : 'border-border bg-white')}>
      <input type="checkbox" checked={checked} onChange={onChange} className="h-6 w-6 shrink-0 accent-primary" />
      <span className="min-w-0"><span className="block font-semibold">{label}</span>{detail && <span className="block text-sm text-text-secondary">{detail}</span>}</span>
    </label>
  )
}

function PerfilForm({ profile, updateProfile, signOut }) {
  const { socio, cursos, cursos_disponibles: cursosDisponibles, acles, acles_disponibles: aclesDisponibles } = profile
  const [values, setValues] = useState({
    email: socio.email || '',
    telefono: socio.telefono || '',
    direccion: socio.direccion || '',
    comuna: socio.comuna || '',
  })
  const availableIds = new Set(cursosDisponibles.map((curso) => curso.id))
  const [cursoIds, setCursoIds] = useState(() => cursos.filter((curso) => availableIds.has(curso.id)).map((curso) => curso.id))
  const [participaAcle, setParticipaAcle] = useState(acles.length > 0)
  const [acleIds, setAcleIds] = useState(() => acles.map((acle) => acle.id))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  // Cursos de años anteriores o inactivos: se muestran, pero no se editan desde el portal.
  const historicos = cursos.filter((curso) => !availableIds.has(curso.id))

  function setField(name, value) {
    setValues((current) => ({ ...current, [name]: value }))
    setMessage('')
  }

  function toggle(list, setList, id) {
    setList((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
    setMessage('')
  }

  function validate() {
    const next = {}
    const email = values.email.trim()
    const telefono = values.telefono.trim()
    if (email && !EMAIL_PATTERN.test(email)) next.email = 'Ingresa un email válido.'
    if (telefono && !PHONE_PATTERN.test(telefono)) next.telefono = 'Ingresa un teléfono válido, por ejemplo +56 9 1234 5678.'
    return next
  }

  async function submit(event) {
    event.preventDefault()
    if (saving) return
    const nextErrors = validate()
    setErrors(nextErrors)
    setMessage('')
    setError('')
    if (Object.keys(nextErrors).length) return
    setSaving(true)
    try {
      await updateProfile({
        email: values.email,
        telefono: values.telefono,
        direccion: values.direccion,
        comuna: values.comuna,
        curso_ids: cursoIds,
        acle_ids: participaAcle ? acleIds : [],
      })
      setMessage('Información actualizada correctamente.')
    } catch (saveError) {
      // 401: el contexto ya cerró la sesión y la página redirige a /socio/login.
      if (saveError.status !== 401) setError(saveError.status === 400 && saveError.message ? saveError.message : SAVE_ERROR)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-white/95 px-4 pt-safe backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-3xl items-center justify-between gap-3 py-2">
          <Link to="/socio/inicio" className="inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm font-semibold text-primary"><Icon name="chevronLeft" className="h-4 w-4" />Volver</Link>
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Portal del socio</p>
          <button type="button" onClick={signOut} className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border px-3 text-sm font-semibold text-primary" aria-label="Cerrar sesión"><Icon name="logout" className="h-4 w-4" /><span>Salir</span></button>
        </div>
      </header>
      <form onSubmit={submit} noValidate className="mx-auto max-w-3xl space-y-6 px-4 pt-6 pb-safe-page">
        <h1 className="text-2xl font-bold sm:text-3xl">Mi perfil</h1>

        <section className="space-y-3">
          <SectionTitle>Datos personales</SectionTitle>
          <div className="space-y-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
            <dl className="grid gap-4 sm:grid-cols-2"><ReadOnly label="Nombre completo" value={socio.nombre} /><ReadOnly label="RUT" value={socio.rut} /></dl>
            <label className="block text-sm font-semibold">Email<input type="email" inputMode="email" autoComplete="email" maxLength={254} value={values.email} onChange={(event) => setField('email', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.email)} />{errors.email && <span className="mt-1.5 block text-sm font-normal text-danger">{errors.email}</span>}</label>
            <label className="block text-sm font-semibold">Teléfono<input type="tel" inputMode="tel" autoComplete="tel" maxLength={25} value={values.telefono} onChange={(event) => setField('telefono', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.telefono)} />{errors.telefono && <span className="mt-1.5 block text-sm font-normal text-danger">{errors.telefono}</span>}</label>
            <label className="block text-sm font-semibold">Dirección<input type="text" autoComplete="street-address" maxLength={200} value={values.direccion} onChange={(event) => setField('direccion', event.target.value)} className={inputClass} /></label>
            <label className="block text-sm font-semibold">Comuna<input type="text" autoComplete="address-level2" maxLength={80} value={values.comuna} onChange={(event) => setField('comuna', event.target.value)} className={inputClass} /></label>
          </div>
        </section>

        <section className="space-y-3">
          <SectionTitle>Cursos de mis hijos</SectionTitle>
          <div className="space-y-3 rounded-2xl border border-border bg-white p-5 shadow-sm">
            <p className="text-sm text-text-secondary">Selecciona los cursos en los que actualmente tienes hijos o hijas.</p>
            {cursosDisponibles.length ? <div className="grid gap-2 sm:grid-cols-2">{cursosDisponibles.map((curso) => <CheckRow key={curso.id} checked={cursoIds.includes(curso.id)} onChange={() => toggle(cursoIds, setCursoIds, curso.id)} label={curso.nombre} detail={[curso.nivel, curso.seccion].filter(Boolean).join(' · ')} />)}</div> : <p className="text-sm text-text-secondary">No hay cursos disponibles por el momento.</p>}
            {historicos.length > 0 && <div className="border-t border-border pt-3"><p className="text-xs font-bold uppercase tracking-wide text-text-secondary">Otros cursos asociados</p><p className="mt-1 text-sm text-text-secondary">{historicos.map((curso) => [curso.nombre, curso.anio].filter(Boolean).join(' ')).join(', ')}</p></div>}
          </div>
        </section>

        <section className="space-y-3">
          <SectionTitle>Actividades ACLE</SectionTitle>
          <fieldset className="space-y-3 rounded-2xl border border-border bg-white p-5 shadow-sm">
            <legend className="sr-only">Participación en actividades ACLE</legend>
            <p className="font-semibold">¿Alguno de tus hijos participa actualmente en una actividad ACLE?</p>
            <div className="grid grid-cols-2 gap-2">
              {[['Sí', true], ['No', false]].map(([label, value]) => <label key={label} className={'flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 py-3 font-semibold transition ' + (participaAcle === value ? 'border-secondary bg-secondary/10' : 'border-border bg-white')}><input type="radio" name="participa-acle" checked={participaAcle === value} onChange={() => { setParticipaAcle(value); setMessage('') }} className="h-5 w-5 accent-primary" />{label}</label>)}
            </div>
            {participaAcle && <div className="grid gap-2 pt-1 sm:grid-cols-2">{aclesDisponibles.map((acle) => <CheckRow key={acle.id} checked={acleIds.includes(acle.id)} onChange={() => toggle(acleIds, setAcleIds, acle.id)} label={acle.nombre} />)}</div>}
          </fieldset>
        </section>

        {message && <div className="rounded-2xl border border-success/30 bg-success/10 p-4 text-sm font-semibold text-success" role="status">{message}</div>}
        {error && <div className="rounded-2xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}

        <div className="sticky bottom-0 -mx-4 border-t border-border bg-white/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
          <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row-reverse">
            <button type="submit" disabled={saving} className="min-h-12 w-full rounded-xl bg-primary px-5 py-3 text-base font-semibold text-white shadow-lg shadow-primary/20 transition hover:bg-primary-hover disabled:opacity-60 sm:w-auto">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
            <Link to="/socio/inicio" className="inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-border px-5 py-3 font-semibold text-primary sm:w-auto">Volver</Link>
          </div>
        </div>
      </form>
    </div>
  )
}

export default function SocioPerfilPage() {
  const { profile, loading, token, signOut, updateProfile } = useSocioPortal()
  if (loading) return <div className="grid min-h-dvh place-items-center bg-background text-text-secondary" role="status">Cargando…</div>
  if (!token || !profile) return <Navigate to="/socio/login" replace />
  return <PerfilForm profile={profile} updateProfile={updateProfile} signOut={signOut} />
}
